import React, { Component } from 'react';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import { HTTPService } from "../../../services/httpService";
import ServicesAPI from "../../../services/servicesApiV2";
import { ASSET_TYPES } from '../../../util/assetType';
import { isMarineEnabled } from '../../../util/marineCapability';
import {
  parseCellKey,
  indexOverrides,
  indexTrimShadows,
  diffOverrides,
} from '../../../util/templateOverrides';
import {
  getTemplatePrices,
  saveTemplatePrices,
  deleteTemplatePrices,
} from '../../../services/templatePricingService';
import { PriceCell } from './PriceCell';
import './serviceCatalog.css';

const API = new HTTPService();
const SIZES = ['S', 'M', 'L', 'XL'];

export class TemplateModal extends Component {
    state = {
        templateName: "",
        selectedServices: new Set(),
        // What was actually loaded (server baseline for an edit, or the empty
        // starting point for a new template) — a SEPARATE instance from
        // `selectedServices`/`templateName` above, the same way loadedOverrides
        // is kept separate from editedOverrides. This is what the close-guard
        // diffs the live state against to decide whether membership/name
        // changed, independent of the price-override diff.
        loadedTemplateName: "",
        loadedSelectedServices: new Set(),
        activeTab: "",
        serviceCategories: [],
        // Which MARKET the listed services sell into. 'auto' | 'marine' —
        // master_services.asset_type. Deliberately Auto/Marine, not Road/Water:
        // Road/Water names the ASSET being worked on, this names the service.
        assetTypeFilter: 'auto',
        validMasterServiceIds: new Set(), // Track valid IDs from the catalog
        loading: false,
        errors: {},
        saving: false,

        // Per-template price overrides. loadedOverrides is the server
        // baseline (what a save diffs against); editedOverrides is the
        // in-progress edit. They start equal but must stay TWO Map
        // instances — sharing one would let an in-place `.set` corrupt the
        // baseline and pin the diff at "nothing changed" forever.
        loadedOverrides: new Map(),
        editedOverrides: new Map(),
        shadowIndex: new Map(),
        // Non-null when the price read failed — an empty Map alone would be
        // indistinguishable from "this template genuinely has no
        // overrides", and saving from that state would diff every existing
        // server row into a delete.
        pricesNotice: null,

        // Set once the create-a-template POST has actually landed, so a
        // retry after a price-phase failure never re-POSTs — without this,
        // the only enabled button after "created, but prices failed" would
        // create a SECOND, duplicate template. createdTemplateId is the id
        // that POST resolved to, read via `?? ` off the create response
        // (see handleSubmit, ~:617-620) — POST /templates has returned
        // template_id since at least v6.0, so this is expected to always be
        // non-null in practice; the genuinely-absent case is still guarded
        // (see the `!newTemplateId` backstop in handleSubmit, ~:642) but is
        // not a path that's expected to run. templateCreated is the
        // authoritative "don't POST again" flag, independent of that id.
        templateCreated: false,
        createdTemplateId: null
    };

    async componentDidMount() {
        await this.loadServiceCatalog();
    }

    componentDidUpdate(prevProps) {
        // Reset form when modal opens/closes or action changes
        if (this.props.show && !prevProps.show) {
            this.resetForm();
        }

        // Load template data when editing
        if (this.props.action === "edit" && this.props.template !== prevProps.template) {
            this.loadTemplateData();
            // Price overrides are per-template. loadServiceCatalog's own
            // getTemplatePrices call (below) only covers the case where the
            // modal happens to already be in edit mode when it first mounts
            // — normally it mounts with action "" before any template is
            // chosen (TemplateList renders it once, unconditionally, and
            // toggles `show`/`template`/`action` on it rather than
            // remounting) — so re-run it here for every template actually
            // opened for editing.
            this.loadTemplateOverrides();
        }
    }

    // Load service catalog from API (using v2 endpoints)
    async loadServiceCatalog() {
        try {
            this.setState({ loading: true });

            // Load categories, master services, and pricing using new API.
            // Trim overrides feed ONLY the shadow-warning tooltip — a nice-to
            // -have, not load-bearing — so its failure is caught on its own
            // (matches catalogValidation.js's loadServiceCatalog, the other
            // caller of this same endpoint) rather than being allowed to
            // reject the whole Promise.all and drop the entire modal into
            // mock data, which also leaves validMasterServiceIds empty and
            // makes the real save impossible.
            const [categories, masterServices, installerServices, trimOverrideRows] = await Promise.all([
                ServicesAPI.getServiceCategories(),
                ServicesAPI.getMasterServicesByInstaller(this.props.typeId),
                ServicesAPI.getInstallerServices(this.props.typeId),
                ServicesAPI.getAllTrimOverrides(this.props.typeId).catch((err) => {
                    console.error('Failed to load trim overrides (shadow tooltip will be blank):', err);
                    return [];
                })
            ]);

            // Build pricing lookup by master_service_id
            const pricingLookup = {};
            installerServices.forEach(record => {
                const masterId = record.master_service_id;
                if (!pricingLookup[masterId]) {
                    pricingLookup[masterId] = { RETAIL: {}, WHOLE: {} };
                }
                const price = record.price ?? record.service_cost ?? 0;
                const catalog = record.catalog; // RETAIL or WHOLE
                const size = record.vehicle_size; // S, M, L, XL
                if (catalog && size) {
                    pricingLookup[masterId][catalog][size] = price;
                }
            });

            // Build set of valid master service IDs
            const validMasterServiceIds = new Set(masterServices.map(s => s.id));

            // Build category lookup
            const categoryLookup = {};
            categories.forEach(cat => {
                categoryLookup[cat.category_id] = cat.category_name;
            });

            // Filter out Custom services and group by category
            const categoriesMap = {};

            masterServices.forEach(service => {
                const serviceName = service.service_name || '';

                // Skip services with "Custom" in the name
                if (serviceName.toLowerCase().includes('custom')) {
                    return;
                }

                const categoryName = service.category_name || categoryLookup[service.category_id] || 'Other';

                if (!categoriesMap[categoryName]) {
                    categoriesMap[categoryName] = {
                        category_name: categoryName,
                        services: []
                    };
                }

                categoriesMap[categoryName].services.push({
                    master_service_id: service.id,
                    description: service.service_name,
                    coverage_level: service.coverage_level,
                    notes: service.notes,
                    // Kept so the Auto/Marine filter has something to filter ON.
                    // This shaped entry previously dropped it, which is why the
                    // list could only ever show everything at once.
                    asset_type: service.asset_type || 'auto',
                    pricing: pricingLookup[service.id] || { RETAIL: {}, WHOLE: {} }
                });
            });

            const serviceCategories = Object.values(categoriesMap);

            // Seed the tab from the FILTERED list, not the raw one: a category
            // whose services are all marine is not on screen under the default
            // Auto filter, and pointing activeTab at it renders an empty panel
            // with no tab highlighted.
            const firstVisible = serviceCategories.find(c =>
                (c.services || []).some(s => (s.asset_type || 'auto') === this.state.assetTypeFilter)
            );

            this.setState({
                serviceCategories,
                validMasterServiceIds,
                activeTab: firstVisible?.category_name || "",
                shadowIndex: indexTrimShadows(trimOverrideRows),
                loading: false
            });

            // Covers the modal already being in edit mode at construction
            // time (see componentDidUpdate for the normal path, which fires
            // once a template is actually chosen).
            if (this.props.action === "edit") {
                await this.loadTemplateOverrides();
            }

        } catch (error) {
            console.error("Error loading service catalog:", error);
            console.error("Error details:", error.response?.data);

            this.setState({
                serviceCategories: [],
                loading: false,
                shadowIndex: new Map(),
                loadedOverrides: new Map(),
                editedOverrides: new Map(),
                errors: { ...this.state.errors, loadError: 'Failed to load service catalog' }
            });

            // Load mock data as fallback
            this.loadMockServiceData();
        }
    }

    // Per-template absolute price overrides. Only meaningful once editing an
    // existing template (a new one has no template_id yet). A failed read
    // degrades to an empty map plus a visible, non-blocking notice — it must
    // NEVER silently look like "no overrides", because saving from that
    // state would diff every existing server row into a delete.
    //
    // Gated behind `loading` (the same flag that hides the whole modal body
    // while the catalog itself fetches) so the matrix isn't interactive
    // until this resolves — without that, a price typed into a cell during
    // the fetch would be silently overwritten the instant it completes,
    // since success reseats `editedOverrides` unconditionally.
    async loadTemplateOverrides() {
        const templateId = this.props.template && this.props.template.template_id;
        if (!(this.props.action === "edit" && templateId)) {
            this.setState({
                loadedOverrides: new Map(),
                editedOverrides: new Map(),
                pricesNotice: null
            });
            return;
        }

        this.setState({ loading: true });

        try {
            const priceRows = await getTemplatePrices(this.props.typeId, templateId);
            const loaded = indexOverrides(priceRows);
            // Two INDEPENDENT Map instances — `loaded` itself is never
            // handed to more than one setter.
            this.setState({
                loadedOverrides: loaded,
                editedOverrides: new Map(loaded),
                pricesNotice: null,
                loading: false
            });
        } catch (error) {
            console.error("Failed to load template price overrides:", error);
            this.setState({
                loadedOverrides: new Map(),
                editedOverrides: new Map(),
                // Blocking, not just a warning: with the server's existing
                // rows unknown, editedOverrides/loadedOverrides are both
                // empty by necessity — diffOverrides against that pair
                // produces no deletes even for a service that gets
                // unchecked here, so a save would silently ORPHAN whatever
                // override rows the server actually still has for it.
                // There's no diff that can be trusted from this state, so
                // saving is disabled entirely (see handleSubmit / the
                // submit button's `disabled`) rather than risking that.
                pricesNotice: 'Existing price overrides could not be loaded — showing base catalog prices only. Saving is disabled until this is resolved; close and reopen this template to try again.',
                loading: false
            });
        }
    }

    // Mock data for development (remove when API is ready)
    loadMockServiceData() {
        const mockCategories = [
            {
                category_name: "Ceramic Coating",
                services: [
                    { master_service_id: 1, description: "Basic Ceramic Coating" },
                    { master_service_id: 2, description: "Premium Ceramic Coating" },
                    { master_service_id: 3, description: "Ceramic Coating - Full Vehicle" },
                    { master_service_id: 4, description: "Ceramic Coating - Partial" }
                ]
            },
            {
                category_name: "PPF",
                services: [
                    { master_service_id: 5, description: "PPF Full Vehicle" },
                    { master_service_id: 6, description: "PPF Front End" },
                    { master_service_id: 7, description: "PPF Hood" },
                    { master_service_id: 8, description: "PPF Bumper" }
                ]
            },
            {
                category_name: "Window Tint",
                services: [
                    { master_service_id: 54, description: "Front Doors Tint" },
                    { master_service_id: 56, description: "Rear Doors Tint" },
                    { master_service_id: 58, description: "Windshield Tint" }
                ]
            },
            {
                category_name: "Vinyl Wrap",
                services: [
                    { master_service_id: 10, description: "Full Vehicle Wrap" },
                    { master_service_id: 11, description: "Partial Wrap" },
                    { master_service_id: 12, description: "Racing Stripes" }
                ]
            },
            {
                category_name: "Other Services",
                services: [
                    { master_service_id: 13, description: "Chrome Delete" },
                    { master_service_id: 14, description: "Paint Correction" },
                    { master_service_id: 15, description: "Interior Detail" }
                ]
            }
        ];

        //console.log('📝 Loading mock service data as fallback');
        this.setState({ 
            serviceCategories: mockCategories,
            activeTab: mockCategories[0]?.category_name || "",
            loading: false 
        });
    }

    // Reset form to initial state
    resetForm() {
        this.setState({
            templateName: "",
            selectedServices: new Set(),
            // A brand-new template's baseline IS "nothing loaded yet" — matches
            // the initial templateName/selectedServices above so a fresh modal
            // never reads as dirty before the user has touched anything.
            loadedTemplateName: "",
            loadedSelectedServices: new Set(),
            activeTab: this.state.serviceCategories[0]?.category_name || "",
            errors: {},
            // Cleared here so a leftover edit session's overrides can't leak
            // into a fresh "Add New Template" open — the edit branch below
            // (componentDidUpdate) reseats these from the server right after
            // when the new open is itself an edit.
            loadedOverrides: new Map(),
            editedOverrides: new Map(),
            pricesNotice: null,
            templateCreated: false,
            createdTemplateId: null
        });
    }

    // Load template data for editing
    loadTemplateData() {
        if (this.props.template && this.props.action === "edit") {
            const { template } = this.props;

            console.log('DEBUG - Template being edited:', template);
            console.log('DEBUG - Template services:', template.services);

            // Load existing template services (they come from the detailed template data)
            const selectedServices = new Set();
            const invalidServices = [];

            if (template.services && Array.isArray(template.services)) {
                template.services.forEach(service => {
                    const serviceId = service.master_service_id;
                    // Only add services that exist in the current catalog
                    if (this.state.validMasterServiceIds.has(serviceId)) {
                        console.log('DEBUG - Adding valid service to selection:', service);
                        selectedServices.add(serviceId);
                    } else {
                        console.warn('DEBUG - Skipping invalid service (not in catalog):', service);
                        invalidServices.push(service);
                    }
                });
            }

            console.log('DEBUG - Valid selected service IDs:', Array.from(selectedServices));
            if (invalidServices.length > 0) {
                console.warn('DEBUG - Template has', invalidServices.length, 'services not in current catalog:', invalidServices);
            }

            this.setState({
                templateName: template.template_name || "",
                selectedServices,
                loadedTemplateName: template.template_name || "",
                // A SEPARATE Set from `selectedServices` — never the same
                // instance, or an in-place mutation of one would silently move
                // the other and pin the close-guard's diff at "nothing changed".
                loadedSelectedServices: new Set(selectedServices)
            });
        }
    }

    // Handle template name change
    handleNameChange = (event) => {
        this.setState({ 
            templateName: event.target.value,
            errors: { ...this.state.errors, templateName: "" }
        });
    };

    // A service's master_service_id has at least one price override in the
    // CURRENT edit (not just the loaded baseline), so unchecking mid-edit is
    // caught too, not just overrides that existed before this session opened.
    hasOverridesForService(masterServiceId) {
        for (const key of this.state.editedOverrides.keys()) {
            if (parseCellKey(key).masterServiceId === masterServiceId) return true;
        }
        return false;
    }

    // "Reset to catalog" — the per-service version of clearing a single cell.
    // Pending only: it edits editedOverrides in memory and never calls the
    // server. Save's existing diffOverrides machinery turns the now-missing
    // keys into DELETEs, the same as if every cell for this service had been
    // cleared by hand. No confirmation — unlike unchecking a service (where
    // price loss is a surprising side-effect of a membership action), this
    // button's label already says exactly what it does and it only touches
    // the one service the user clicked it on.
    handleResetServiceToCatalog = (masterServiceId, event) => {
        // The card itself toggles selection on click — this must never bubble
        // up to that handler.
        event.stopPropagation();
        const next = new Map(this.state.editedOverrides);
        Array.from(next.keys())
            .filter((key) => parseCellKey(key).masterServiceId === masterServiceId)
            .forEach((key) => next.delete(key));
        this.setState({ editedOverrides: next });
    };

    // Handle service selection toggle
    handleServiceToggle = (serviceId) => {
        // Frozen once a create has partially succeeded: the template row
        // already exists server-side, and a retry from here re-sends ONLY the
        // price diff (see handleSubmit) — a membership flip made between the
        // failure and the retry would never reach the server, silently
        // violating "only checked services are saved". Edit mode never sets
        // templateCreated, so this never fires there.
        if (this.state.templateCreated && this.props.action !== "edit") return;

        const isCurrentlySelected = this.state.selectedServices.has(serviceId);

        // Unchecking a service that carries overrides is recoverable — the
        // overrides are discarded at SAVE, not here, so re-checking the box
        // before saving brings them right back. This is only the warning.
        if (isCurrentlySelected && this.hasOverridesForService(serviceId)) {
            const proceed = window.confirm(
                "This service has custom prices set for this template. Unchecking it will remove those prices when you save — re-check it before saving to keep them. Uncheck anyway?"
            );
            if (!proceed) return;
        }

        const newSelected = new Set(this.state.selectedServices);
        if (isCurrentlySelected) {
            newSelected.delete(serviceId);
        } else {
            newSelected.add(serviceId);
        }
        this.setState({
            selectedServices: newSelected,
            errors: { ...this.state.errors, services: "" }
        });
    };

    // Overrides for services no longer checked — a discard is recoverable up
    // until save (re-checking the box brings them back); this is what makes
    // that discard actually happen, filtering editedOverrides down to only
    // the services in `selectedIds` at save time.
    getEffectiveOverrides(selectedIds) {
        const idSet = new Set(selectedIds);
        const effective = new Map();
        this.state.editedOverrides.forEach((price, key) => {
            if (idSet.has(parseCellKey(key).masterServiceId)) {
                effective.set(key, price);
            }
        });
        return effective;
    }

    // A single cell edit inside the matrix.
    handleOverrideChange = (next) => {
        this.setState({ editedOverrides: next });
    };

    // Handle tab change
    /**
     * Switch which market's services are listed.
     *
     * The active category tab is reset because the categories themselves change
     * — a category that exists for auto may have no marine services at all, and
     * leaving activeTab pointing at a filtered-away tab renders an empty panel
     * with no tab highlighted.
     */
    setAssetTypeFilter = (assetTypeFilter) => {
        if (assetTypeFilter === this.state.assetTypeFilter) return;
        const next = (this.state.serviceCategories || [])
            .filter(c => (c.services || []).some(s => (s.asset_type || 'auto') === assetTypeFilter));
        this.setState({
            assetTypeFilter,
            activeTab: next[0]?.category_name || "",
        });
    };

    handleTabChange = (categoryName) => {
        this.setState({ activeTab: categoryName });
    };

    // Validate form
    validateForm() {
        const errors = {};

        if (!this.state.templateName.trim()) {
            errors.templateName = "Template name is required";
        }

        // Check that at least one VALID service is selected
        const validSelectedCount = Array.from(this.state.selectedServices)
            .filter(id => this.state.validMasterServiceIds.has(id)).length;

        if (this.state.selectedServices.size === 0) {
            errors.services = "At least one service must be selected";
        } else if (validSelectedCount === 0) {
            errors.services = "None of the selected services are valid. Please select services from the catalog.";
        }

        this.setState({ errors });
        return Object.keys(errors).length === 0;
    }

    // Handle form submission
    handleSubmit = async () => {
        if (!this.validateForm()) {
            return;
        }

        // Backstop for the submit button's own `disabled` (see render) — a
        // degraded price read means loadedOverrides/editedOverrides are both
        // empty by necessity (the server's actual rows are unknown), so no
        // diff computed from them can be trusted; saving from here could
        // silently orphan override rows the server still has.
        if (this.state.pricesNotice) {
            return;
        }

        this.setState({ saving: true });

        // Filter selected services to only include valid master service IDs
        const allSelectedServices = Array.from(this.state.selectedServices);
        const validServices = allSelectedServices.filter(id => this.state.validMasterServiceIds.has(id));
        const invalidServices = allSelectedServices.filter(id => !this.state.validMasterServiceIds.has(id));

        if (invalidServices.length > 0) {
            console.warn('DEBUG - Filtering out invalid service IDs:', invalidServices);
        }

        // Ensure we're sending the correct field name
        const templateData = {
            template_name: this.state.templateName.trim(),
            services: validServices, // Only send valid service IDs
            type_id: parseInt(this.props.typeId) // Use type_id to match what backend expects
        };

        console.log('DEBUG - Sending template data to API:', templateData);
        console.log('DEBUG - Service IDs being sent:', templateData.services);
        console.log('DEBUG - Filtered out invalid IDs:', invalidServices);

        // A service unchecked at save time discards its overrides here —
        // handleServiceToggle's warning didn't touch editedOverrides, so
        // this is the actual discard point (recoverable up until now by
        // re-checking the box). validServices IS the final checked+valid
        // membership, so it doubles as the filter for which cells are still
        // "on the template".
        const effectiveOverrides = this.getEffectiveOverrides(validServices);

        try {
            if (this.props.action === "edit") {
                const templateId = this.props.template.template_id;

                // Phase 1 of 2: membership.
                await API.put(`/templates/${this.props.typeId}/${templateId}`, templateData);

                // Phase 2 of 2: prices — an independent write. The template
                // row above is already saved regardless of what happens here,
                // so a failure here must say exactly that, not report a
                // clean save.
                const { upserts, deletes } = diffOverrides(this.state.loadedOverrides, effectiveOverrides);
                try {
                    if (upserts.length) await saveTemplatePrices(this.props.typeId, templateId, upserts);
                    if (deletes.length) await deleteTemplatePrices(this.props.typeId, templateId, deletes);
                } catch (priceError) {
                    console.error("Error saving template price overrides:", priceError);
                    const msg = priceError.response?.data?.error || priceError.response?.data?.message || priceError.message || 'unknown error';
                    this.setState({
                        saving: false,
                        errors: { submit: `Template updated, but price changes could not be saved: ${msg}. Save again to retry.` }
                    });
                    return;
                }

            } else {
                let newTemplateId = this.state.createdTemplateId;

                if (!this.state.templateCreated) {
                    // Phase 1 of 2: create. The response used to be
                    // discarded entirely (`await API.post('/templates',
                    // templateData);`, no destructure) — read it now, since
                    // phase 2 needs the new template_id.
                    const createResponse = await API.post('/templates', templateData);
                    console.log('DEBUG - Template creation response:', JSON.stringify(createResponse?.data, null, 2));

                    newTemplateId =
                        createResponse?.data?.data?.template_id ??
                        createResponse?.data?.template_id ??
                        null;

                    // The template row exists on the server now, regardless
                    // of what happens next. Persist BEFORE attempting the
                    // price write so a retry — the same button, re-clicked
                    // after a price-phase failure below — skips this POST
                    // entirely. Without this, the only enabled control after
                    // a partial failure would create a SECOND, duplicate
                    // template.
                    this.setState({ templateCreated: true, createdTemplateId: newTemplateId });
                }

                // Phase 2 of 2: prices.
                const { upserts } = diffOverrides(new Map(), effectiveOverrides);
                if (upserts.length) {
                    // The create response is expected to always carry an id
                    // (POST /templates has returned template_id since at
                    // least v6.0 — see the `newTemplateId` read above), so
                    // this is a backstop rather than a path that runs in
                    // practice: if it's ever genuinely absent, the price
                    // write must not proceed and the user must be told the
                    // template was created but prices were not attached.
                    if (!newTemplateId) {
                        this.setState({
                            saving: false,
                            errors: { submit: `Template "${templateData.template_name}" was created, but its id could not be determined, so its custom prices were not saved. Close this dialog and edit the template from the list to add them.` }
                        });
                        return;
                    }
                    try {
                        await saveTemplatePrices(this.props.typeId, newTemplateId, upserts);
                    } catch (priceError) {
                        console.error("Error saving prices for newly created template:", priceError);
                        const msg = priceError.response?.data?.error || priceError.response?.data?.message || priceError.message || 'unknown error';
                        this.setState({
                            saving: false,
                            errors: { submit: `Template "${templateData.template_name}" was created, but its custom prices could not be saved: ${msg}. Try again below. The template name and selected services are locked until the prices save.` }
                        });
                        return;
                    }
                }
            }

            // Full success — close modal and refresh parent data.
            this.setState({ saving: false });
            this.props.onHide();
            if (this.props.onSave) {
                this.props.onSave();
            }

        } catch (error) {
            console.error("Error saving template:", error);
            console.error("Error response:", error.response?.data);

            // Handle specific error responses
            let errorMessage = "Failed to save template. Please try again.";
            if (error.response && error.response.data && error.response.data.error) {
                errorMessage = error.response.data.error;
                // The server's duplicate-name check is case-insensitive, so
                // two visually different names (e.g. "Standard Pricing" and
                // "standard pricing") can collide — say so explicitly, since
                // the server's own message alone doesn't explain why a name
                // that looks unique was refused. Use a neutral example name,
                // never a real customer's — this renders in every installer's
                // UI.
                if (/template name already exists/i.test(errorMessage)) {
                    errorMessage = "Template name already exists (name matching ignores letter case, so \"Standard Pricing\" and \"standard pricing\" count as the same name). Choose a different name.";
                }
            }

            this.setState({
                saving: false,
                errors: {
                    submit: errorMessage
                }
            });
        }
    };

    // Get selected count for a category
    getSelectedCountForCategory(category) {
        return category.services.filter(service =>
            this.state.selectedServices.has(service.master_service_id)
        ).length;
    }

    // Is there anything a close would throw away? Computed from CONTENT, not
    // Map/Set identity — editedOverrides is reseated on every keystroke (see
    // handleOverrideChange), so a fresh Map with the same entries as loaded
    // (e.g. typing "8." then clearing it back out) must read as clean, not
    // dirty. diffOverrides already does exactly that comparison; reusing it
    // here avoids a second, divergent notion of "changed".
    hasPendingChanges() {
        const { upserts, deletes } = diffOverrides(this.state.loadedOverrides, this.state.editedOverrides);
        if (upserts.length > 0 || deletes.length > 0) return true;

        if (this.state.templateName.trim() !== this.state.loadedTemplateName.trim()) return true;

        const { selectedServices, loadedSelectedServices } = this.state;
        if (selectedServices.size !== loadedSelectedServices.size) return true;
        for (const id of selectedServices) {
            if (!loadedSelectedServices.has(id)) return true;
        }

        return false;
    }

    // Both the footer Cancel and the header X (Modal's own `onHide`, which
    // also covers Escape/backdrop-click) route through here so they can never
    // diverge. Never guards mid-save — `saving` already disables Cancel, and
    // a save in flight isn't "unsaved changes" the user is about to lose.
    handleRequestClose = () => {
        if (!this.state.saving && this.hasPendingChanges()) {
            const proceed = window.confirm(
                "You have unsaved changes. Close without saving?"
            );
            if (!proceed) return;
        }
        this.props.onHide();
    };

    render() {
        const { show, action } = this.props;
        const {
            templateName,
            selectedServices,
            activeTab,
            serviceCategories: allServiceCategories,
            assetTypeFilter,
            loading,
            errors,
            saving,
            editedOverrides,
            shadowIndex,
            pricesNotice,
            templateCreated
        } = this.state;

        // Filter by market, then drop categories the filter emptied — a category
        // tab with nothing behind it is worse than no tab. When marine is not
        // visible the filter is inert and every service is 'auto' anyway.
        const serviceCategories = allServiceCategories
            .map(category => ({
                ...category,
                services: (category.services || []).filter(
                    svc => (svc.asset_type || 'auto') === assetTypeFilter
                ),
            }))
            .filter(category => category.services.length > 0);

        const isEditing = action === "edit";
        const totalSelected = selectedServices.size;

        return (
            <Modal show={show} onHide={this.handleRequestClose} size="lg" centered>
                <Modal.Header closeButton style={{ backgroundColor: 'var(--color-bg-panel)' }}>
                    <Modal.Title>
                        {isEditing ? 'Edit Template' : 'Create New Template'}
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body style={{ padding: 0 }}>
                    {loading ? (
                        <div className="text-center py-5">
                            <div className="spinner-border" role="status">
                                <span className="sr-only">Loading...</span>
                            </div>
                            <p className="mt-2 text-muted">Loading service catalog...</p>
                        </div>
                    ) : (
                        <>
                            {/* Template Name Section */}
                            <div className="px-4 py-3 border-bottom bg-light">
                                <Form.Group>
                                    <Form.Label className="font-weight-bold">Template Name</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="Enter template name..."
                                        value={templateName}
                                        onChange={this.handleNameChange}
                                        isInvalid={!!errors.templateName}
                                        // Frozen once a create has partially succeeded — see
                                        // handleServiceToggle's guard for why the name can't
                                        // safely change here anymore either.
                                        disabled={templateCreated}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors.templateName}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </div>

                            {/* Service Selection Section */}
                            <div className="px-4 py-3">
                                <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap" style={{ gap: '8px' }}>
                                    <h6 className="mb-0 font-weight-bold">Select Services</h6>
                                    <div className="d-flex align-items-center" style={{ gap: '8px' }}>
                                        <small className="text-muted">
                                            {totalSelected} service{totalSelected !== 1 ? 's' : ''} selected
                                        </small>
                                    </div>
                                </div>

                                {errors.services && (
                                    <div className="alert alert-danger py-2 mb-3">
                                        {errors.services}
                                    </div>
                                )}

                                {errors.loadError && (
                                    <div className="alert alert-warning py-2 mb-3">
                                        {errors.loadError} - Using sample data for demonstration.
                                    </div>
                                )}

                                {pricesNotice && (
                                    <div className="alert alert-warning py-2 mb-3">
                                        <i className="fa-sharp fa-regular fa-triangle-exclamation" /> {pricesNotice}
                                    </div>
                                )}

                                {/* Auto / Marine — the MARKET a service sells into, which is
                                    what master_services.asset_type records. Road/Water is the
                                    vehicle vocabulary and belongs on the asset picker, not here.

                                    Gated on isMarineEnabled for the same reason the vehicle
                                    catalog's toggle is: without it, every automotive installer
                                    gets a Marine tab that is permanently empty. */}
                                {isMarineEnabled() && (
                                    <div className="text-center mb-3">
                                        <div className="jv-segmented" role="group" aria-label="Service asset type">
                                            <button
                                                type="button"
                                                aria-pressed={assetTypeFilter === ASSET_TYPES.AUTO}
                                                className={`jv-segmented__btn ${assetTypeFilter === ASSET_TYPES.AUTO ? 'is-active' : ''}`}
                                                onClick={() => this.setAssetTypeFilter(ASSET_TYPES.AUTO)}
                                            >
                                                Auto
                                            </button>
                                            <button
                                                type="button"
                                                aria-pressed={assetTypeFilter === ASSET_TYPES.MARINE}
                                                className={`jv-segmented__btn ${assetTypeFilter === ASSET_TYPES.MARINE ? 'is-active' : ''}`}
                                                onClick={() => this.setAssetTypeFilter(ASSET_TYPES.MARINE)}
                                            >
                                                Marine
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {/* Category Tabs */}
                                <div style={{ height: '450px' }}>
                                    {serviceCategories.length > 0 ? (
                                        <>
                                            <div className="border-bottom mb-3">
                                                <nav className="nav" style={{ flexWrap: 'nowrap', overflowX: 'auto', borderBottom: '1px solid var(--color-border)' }}>
                                                    {serviceCategories.map(category => {
                                                        const categorySelectedCount = this.getSelectedCountForCategory(category);
                                                        const isActive = activeTab === category.category_name;
                                                        
                                                        return (
                                                            <button
                                                                key={category.category_name}
                                                                onClick={() => this.handleTabChange(category.category_name)}
                                                                className="nav-link px-3 py-2"
                                                                style={{ 
                                                                    whiteSpace: 'nowrap', 
                                                                    border: 'none',
                                                                    backgroundColor: 'transparent',
                                                                    color: isActive ? 'var(--color-info)' : 'var(--color-text-secondary)',
                                                                    fontWeight: isActive ? '600' : '400',
                                                                    position: 'relative',
                                                                    borderBottom: isActive ? '3px solid var(--color-info)' : '3px solid transparent',
                                                                    transition: 'all 0.2s ease-in-out',
                                                                    cursor: 'pointer',
                                                                    outline: 'none',
                                                                    marginBottom: '-1px' // To overlap the border-bottom of the container
                                                                }}
                                                                onMouseEnter={(e) => {
                                                                    if (!isActive) {
                                                                        e.target.style.color = 'var(--color-text-primary)';
                                                                        e.target.style.borderBottomColor = 'var(--color-border)';
                                                                    }
                                                                }}
                                                                onMouseLeave={(e) => {
                                                                    if (!isActive) {
                                                                        e.target.style.color = 'var(--color-text-secondary)';
                                                                        e.target.style.borderBottomColor = 'transparent';
                                                                    }
                                                                }}
                                                            >
                                                                <div className="d-flex align-items-center">
                                                                    <span>{category.category_name}</span>
                                                                    {categorySelectedCount > 0 && (
                                                                        <span 
                                                                            className="badge ml-2" 
                                                                            style={{ 
                                                                                fontSize: '10px',
                                                                                backgroundColor: isActive ? 'var(--color-info)' : 'var(--color-bg-inverse)',
                                                                                color: 'var(--color-text-inverse)'
                                                                            }}
                                                                        >
                                                                            {categorySelectedCount}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </button>
                                                        );
                                                    })}
                                                </nav>
                                            </div>

                                            {/* Tab Content */}
                                            <div style={{ height: '370px', overflowY: 'auto' }}>
                                                {serviceCategories.map(category => (
                                                    activeTab === category.category_name && (
                                                        <div key={category.category_name}>
                                                            {category.services.map(service => {
                                                                const isSelected = selectedServices.has(service.master_service_id);
                                                                const pricing = service.pricing || { RETAIL: {}, WHOLE: {} };
                                                                const hasRetail = SIZES.some(s => pricing.RETAIL[s] !== undefined);
                                                                const hasWhole = SIZES.some(s => pricing.WHOLE[s] !== undefined);
                                                                const hasPricing = hasRetail || hasWhole;

                                                                const formatPrice = (val) => {
                                                                    if (!val && val !== 0) return '-';
                                                                    return `$${Number(val).toLocaleString()}`;
                                                                };

                                                                // PriceCell keys/labels off `svc.id`/`svc.name` — this
                                                                // catalog's services carry `master_service_id`/
                                                                // `description` instead, so shim the two fields it reads.
                                                                const pcSvc = { id: service.master_service_id, name: service.description };

                                                                return (
                                                                    <div
                                                                        key={service.master_service_id}
                                                                        className="p-3 mb-2"
                                                                        style={{
                                                                            border: '1px solid var(--color-border-light)',
                                                                            borderRadius: '6px',
                                                                            backgroundColor: isSelected ? 'var(--color-info-light)' : 'var(--color-bg-card)',
                                                                            borderColor: isSelected ? 'var(--color-info)' : 'var(--color-border-light)',
                                                                            cursor: templateCreated ? 'default' : 'pointer',
                                                                            transition: 'all 0.2s ease'
                                                                        }}
                                                                        onClick={() => this.handleServiceToggle(service.master_service_id)}
                                                                    >
                                                                        <div className="d-flex align-items-center">
                                                                            <input
                                                                                type="checkbox"
                                                                                checked={isSelected}
                                                                                onChange={() => this.handleServiceToggle(service.master_service_id)}
                                                                                onClick={(e) => e.stopPropagation()}
                                                                                // Frozen once a create has partially succeeded — see
                                                                                // handleServiceToggle's guard.
                                                                                disabled={templateCreated}
                                                                                style={{
                                                                                    marginRight: '12px',
                                                                                    transform: 'scale(1.2)'
                                                                                }}
                                                                            />
                                                                            <label
                                                                                style={{
                                                                                    cursor: templateCreated ? 'default' : 'pointer',
                                                                                    userSelect: 'none',
                                                                                    margin: 0,
                                                                                    fontWeight: isSelected ? '500' : '400',
                                                                                    color: isSelected ? 'var(--color-info-dark)' : 'var(--color-text-primary)',
                                                                                    flex: 1
                                                                                }}
                                                                            >
                                                                                {service.description}
                                                                                {service.coverage_level && (
                                                                                    <span className="badge badge-secondary ml-2" style={{ fontSize: '10px', fontWeight: 'normal' }}>
                                                                                        {service.coverage_level}
                                                                                    </span>
                                                                                )}
                                                                            </label>
                                                                            {/* Gated on isSelected as well as hasOverridesForService: an
                                                                                unchecked card renders the plain catalog number in every
                                                                                cell (the `!isSelected` branch below never shows the
                                                                                override), so this button would otherwise sit next to a
                                                                                table that looks like nothing is overridden — a second,
                                                                                unconfirmed way to lose a price the uncheck warning just
                                                                                promised was still recoverable by re-checking the box. */}
                                                                            {isSelected && this.hasOverridesForService(service.master_service_id) && (
                                                                                <Button
                                                                                    variant="link"
                                                                                    size="sm"
                                                                                    className="p-0 ml-2"
                                                                                    style={{ fontSize: '11px', whiteSpace: 'nowrap' }}
                                                                                    onClick={(e) => this.handleResetServiceToCatalog(service.master_service_id, e)}
                                                                                >
                                                                                    Reset to catalog
                                                                                </Button>
                                                                            )}
                                                                        </div>
                                                                        {hasPricing && (
                                                                            // The card's onClick toggles selection — every
                                                                            // input below must never let that click bubble
                                                                            // up, or typing a price switches the service
                                                                            // off. One stopPropagation here covers all of
                                                                            // them, the same way the checkbox above does it
                                                                            // for itself individually.
                                                                            <div
                                                                                style={{ marginTop: '6px' }}
                                                                                onClick={(e) => e.stopPropagation()}
                                                                            >
                                                                                <table style={{ fontSize: '11px', color: 'var(--color-text-secondary)', borderCollapse: 'collapse', width: '100%' }}>
                                                                                    <thead>
                                                                                        <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
                                                                                            <th style={{ padding: '2px 6px', fontWeight: '600', textAlign: 'left', width: '60px' }}></th>
                                                                                            {SIZES.map(s => (
                                                                                                <th key={s} style={{ padding: '2px 6px', fontWeight: '600', textAlign: 'right' }}>{s}</th>
                                                                                            ))}
                                                                                        </tr>
                                                                                    </thead>
                                                                                    <tbody>
                                                                                        {hasRetail && (
                                                                                            <tr>
                                                                                                <td style={{ padding: '2px 6px', fontWeight: '600', color: 'var(--color-success)' }}>RETAIL</td>
                                                                                                {SIZES.map(s => (
                                                                                                    isSelected ? (
                                                                                                        <PriceCell
                                                                                                            key={s}
                                                                                                            svc={pcSvc} size={s} catalog="RETAIL"
                                                                                                            base={pricing.RETAIL[s]}
                                                                                                            overrides={editedOverrides}
                                                                                                            shadowIndex={shadowIndex}
                                                                                                            onChange={this.handleOverrideChange}
                                                                                                            readOnly={saving}
                                                                                                        />
                                                                                                    ) : (
                                                                                                        <td key={s} style={{ padding: '2px 6px', textAlign: 'right' }}>
                                                                                                            {formatPrice(pricing.RETAIL[s])}
                                                                                                        </td>
                                                                                                    )
                                                                                                ))}
                                                                                            </tr>
                                                                                        )}
                                                                                        {hasWhole && (
                                                                                            <tr>
                                                                                                <td style={{ padding: '2px 6px', fontWeight: '600', color: 'var(--color-info)' }}>WHOLE</td>
                                                                                                {SIZES.map(s => (
                                                                                                    isSelected ? (
                                                                                                        <PriceCell
                                                                                                            key={s}
                                                                                                            svc={pcSvc} size={s} catalog="WHOLE"
                                                                                                            base={pricing.WHOLE[s]}
                                                                                                            overrides={editedOverrides}
                                                                                                            shadowIndex={shadowIndex}
                                                                                                            onChange={this.handleOverrideChange}
                                                                                                            readOnly={saving}
                                                                                                        />
                                                                                                    ) : (
                                                                                                        <td key={s} style={{ padding: '2px 6px', textAlign: 'right' }}>
                                                                                                            {formatPrice(pricing.WHOLE[s])}
                                                                                                        </td>
                                                                                                    )
                                                                                                ))}
                                                                                            </tr>
                                                                                        )}
                                                                                    </tbody>
                                                                                </table>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    )
                                                ))}
                                            </div>
                                        </>
                                    ) : (
                                        <div className="text-center py-5">
                                            <div className="spinner-border text-muted" role="status">
                                                <span className="sr-only">Loading...</span>
                                            </div>
                                            <p className="mt-2 text-muted">No services available</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </>
                    )}
                </Modal.Body>

                <Modal.Footer style={{ backgroundColor: 'var(--color-bg-panel)' }}>
                    {errors.submit && (
                        <div className="alert alert-danger py-2 mb-2 w-100">
                            {errors.submit}
                        </div>
                    )}
                    
                    <div className="d-flex justify-content-between align-items-center w-100">
                        {/* Counts the WHOLE catalog, not the filtered view. Selections
                            persist across the Auto/Marine toggle, so counting only the
                            visible market could read "3 of 2 services selected". */}
                        <small className="text-muted">
                            {totalSelected} of {allServiceCategories.reduce((acc, cat) => acc + cat.services.length, 0)} services selected
                        </small>
                        <div>
                            <Button variant="secondary" onClick={this.handleRequestClose} disabled={saving}>
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                onClick={this.handleSubmit}
                                // A template with no services is not a template.
                                // validateForm still checks this as a backstop, but a
                                // checkbox list spread across category tabs has nowhere
                                // natural to surface an error — so the button is the
                                // right place to say "not yet". The name stays an inline
                                // field error, because a text input does have somewhere
                                // to put one. `pricesNotice` disables saving entirely — see
                                // loadTemplateOverrides' catch branch for why a degraded
                                // price read can't be trusted to diff safely.
                                disabled={saving || loading || serviceCategories.length === 0 || totalSelected === 0 || !!pricesNotice}
                                className="ml-2"
                            >
                                {saving ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm mr-2" role="status"></span>
                                        {isEditing ? 'Updating...' : (templateCreated ? 'Saving prices...' : 'Creating...')}
                                    </>
                                ) : (
                                    isEditing ? 'Update Template' : (templateCreated ? 'Retry Price Save' : 'Create Template')
                                )}
                            </Button>
                        </div>
                    </div>
                </Modal.Footer>
            </Modal>
        );
    }
}

export default TemplateModal;